/**
 * The fan's own teams on the Scores home: one section card, titled "Your
 * teams", listing the games the favorited teams are playing in the window the
 * feed already returned (Scores UX spec v1.0 section 3.1). Each row carries a
 * league caption above its away line, because this is the one card that mixes
 * leagues. It has no league view to open, so its header has no See all, and it
 * never falls back to last season.
 */
import { type ReactNode } from "react";

import { FAVORITE_TEAMS_TITLE } from "./leagueSelectorContract";
import { ScoreRow } from "./ScoreRow";
import type { typeScoresEventInput } from "./scoreRowModel";
import { countInPlay, ScoresSectionCard } from "./ScoresSectionCard";
import {
  ScoresSectionFailed,
  ScoresSectionLoading,
} from "./ScoresSectionStates";

export type typeFavoriteTeamsSectionCardProps = {
  /** The favorited teams' games, already gathered from the feed response the
   *  pane above holds. This card never fetches, so it can only ever show a
   *  game that feed already carried. */
  events: readonly typeScoresEventInput[];
  /** True when the fan has favorited at least one team. Kept separate from
   *  `events` because on first paint an empty `events` means the same thing
   *  for a fan with no favorites and a fan whose favorites are not playing,
   *  and only the first of those must see nothing at all. */
  hasFavorites: boolean;
  /** True while the feed read that produced `events` is still in flight. */
  isFeedLoading: boolean;
  /** True when the favorites feed read failed (error envelope or thrown). */
  hasFailed?: boolean | undefined;
  /** Refetches the favorites feed. Called by the failed state's Try again. */
  onRetry?: (() => void) | undefined;
  onOpenGame?: ((eventId: string) => void) | undefined;
  /** The fan's followed teams, for the inline star. */
  favoriteTeamIds?: readonly string[] | undefined;
  /** League id to short name ("NFL"), for each row's caption. */
  leagueNameById?: ReadonlyMap<string, string> | undefined;
};

export const FavoriteTeamsSectionCard = (
  props: typeFavoriteTeamsSectionCardProps,
): ReactNode => {
  // This gate is first on purpose. A fan who has favorited no team must see
  // the Scores home exactly as it is without this card, including during the
  // load, so the loading frame must never render above it.
  if (!props.hasFavorites) {
    return null;
  }

  if (props.isFeedLoading) {
    return (
      <ScoresSectionCard
        testId="favorite-teams-section"
        title={FAVORITE_TEAMS_TITLE}
      >
        <ScoresSectionLoading
          testId="favorite-teams-section-loading"
          rows={2}
        />
      </ScoresSectionCard>
    );
  }

  if (props.hasFailed === true) {
    return (
      <ScoresSectionCard
        testId="favorite-teams-section"
        title={FAVORITE_TEAMS_TITLE}
      >
        <ScoresSectionFailed
          testId="favorite-teams-section-failed"
          onRetry={() => {
            props.onRetry?.();
          }}
        />
      </ScoresSectionCard>
    );
  }

  if (props.events.length === 0) {
    return null;
  }

  return (
    <ScoresSectionCard
      testId="favorite-teams-section"
      title={FAVORITE_TEAMS_TITLE}
      liveCount={countInPlay(props.events)}
    >
      {props.events.map((event) => (
        <ScoreRow
          key={event.eventId}
          event={event}
          onOpenGame={props.onOpenGame}
          favoriteTeamIds={props.favoriteTeamIds}
          leagueCaption={
            event.leagueId === undefined
              ? undefined
              : props.leagueNameById?.get(event.leagueId)
          }
        />
      ))}
    </ScoresSectionCard>
  );
};
